import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@/contexts/SubscriptionContext';

interface UsageLimitsPayload {
  new: {
    user_id: string;
    credits_used: number;
    purchased_credits: number;
    chaos_kitchen_count: number;
    cursed_mode_count: number;
    recipe_transform_count: number;
    photo_analysis_count: number;
    recipe_image_gen_count: number;
  };
  old: {
    user_id: string;
    credits_used: number;
    purchased_credits: number;
    chaos_kitchen_count: number;
    cursed_mode_count: number;
    recipe_transform_count: number;
    photo_analysis_count: number;
    recipe_image_gen_count: number;
  };
}

export function useCreditNotifications() {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { effectiveTier: tier, refetchSubscription } = useSubscription();
  const lastCreditsUsed = useRef<number | null>(null);
  const lastPurchasedCredits = useRef<number | null>(null);

  useEffect(() => {
    let userId: string | null = null;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setupSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      userId = user.id;

      channel = supabase
        .channel(`credit-usage-notifications-${crypto.randomUUID()}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'usage_limits',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            const data = payload as unknown as UsageLimitsPayload;
            const newData = data.new;
            const oldData = data.old;

            // Calculate credit changes
            const freeCreditsChange = (newData.credits_used || 0) - (oldData.credits_used || 0);
            const purchasedCreditsChange = (oldData.purchased_credits || 0) - (newData.purchased_credits || 0);

            // Detect which feature was used
            let featureUsed = '';
            let creditsDeducted = 0;

            if ((newData.chaos_kitchen_count || 0) > (oldData.chaos_kitchen_count || 0)) {
              featureUsed = t('creditBadge.features.chaosKitchen');
              creditsDeducted = 1;
            } else if ((newData.cursed_mode_count || 0) > (oldData.cursed_mode_count || 0)) {
              featureUsed = t('creditBadge.features.cursedMode');
              creditsDeducted = 2;
            } else if ((newData.recipe_transform_count || 0) > (oldData.recipe_transform_count || 0)) {
              featureUsed = t('creditBadge.features.recipeTransformer');
              creditsDeducted = 5;
            } else if ((newData.photo_analysis_count || 0) > (oldData.photo_analysis_count || 0)) {
              featureUsed = t('creditBadge.features.photoAnalysis');
              creditsDeducted = 5;
            } else if ((newData.recipe_image_gen_count || 0) > (oldData.recipe_image_gen_count || 0)) {
              featureUsed = t('credits.history.features.imageGeneration');
              creditsDeducted = 5;
            }

            // Only show notification if credits were actually deducted
            if (creditsDeducted > 0 || freeCreditsChange > 0 || purchasedCreditsChange > 0) {
              const usedFromFree = freeCreditsChange > 0;
              const usedFromPurchased = purchasedCreditsChange > 0;

              let sourceText = '';
              if (usedFromFree && usedFromPurchased) {
                sourceText = t('credits.notifications.fromBoth', { 
                  free: freeCreditsChange, 
                  purchased: purchasedCreditsChange 
                });
              } else if (usedFromFree) {
                sourceText = t('credits.notifications.fromFree');
              } else if (usedFromPurchased) {
                sourceText = t('credits.notifications.fromPurchased');
              }

              // Only show toast for non-Chef Master users (they have unlimited)
              if (tier !== 'chef_master') {
                toast({
                  title: featureUsed 
                    ? t('credits.notifications.title', { feature: featureUsed })
                    : t('creditBadge.creditDeducted'),
                  description: sourceText,
                  duration: 4000,
                });
              }

              // Refresh subscription context to update UI
              refetchSubscription();
            }
          }
        )
        .subscribe();
    };

    setupSubscription();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast, t, tier, refetchSubscription]);
}
